import { Component, OnInit, computed, inject, input, output } from '@angular/core';
import { ChoiceComponent } from '../choice/choice.component';
import { IngredientChoice } from '../../models/menu/ingredient-choice.model';
import { ExpandableComponent } from '../../../shared/expandable-component/expandable.component';
import { IconComponent } from '../../../shared/icons/icon.component';
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { CommonModule } from '@angular/common';
import { SizePrice } from '../../models/menu/size-price.model';
import { FormsModule } from '@angular/forms';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { ItemEditorService } from '../../services/item-editor.service';
import { OrderableIngredient } from '../../models/menu/orderable-ingredient.model';
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { OrderedChoice } from '../../models/ordering/ordered-choice.model';
import { OrderedIngredient } from '../../models/ordering/ordered-ingredient.model';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { ChoiceGroupComponent } from "../choice-group/choice-group.component";
import { AuditService } from '../../services/audit.service';
import { DependableIngredientMappingService } from '../../services/dependable-ingr.service';
import { Utils } from '../../utils/utils';
import { OrderableItemService } from '../../services/orderable-item.service';
import { ErrorsService } from '../../services/errors.service';
import { ModalService } from '../../services/modal.service';

interface FilteredChoiceGroup {
  name: string;
  choices: IngredientChoice[];
}

@Component({
  selector: 'cwo-choices',
  standalone: true,
  imports: [
    ChoiceComponent,
    ExpandableComponent,
    IconComponent,
    CommonModule,
    FormsModule,
    TranslocoPipe,
    ChoiceGroupComponent
],
  templateUrl: './choices.component.html',
  styleUrl: './choices.component.scss',
})
export class ChoicesComponent implements OnInit {
  public orderableItemService = inject(OrderableItemService)
  itemEditorService = inject(ItemEditorService);
  private auditService = inject(AuditService);
  private dependableIngredientMappingService = inject(DependableIngredientMappingService);
  private errorsService = inject(ErrorsService);
  private modalService = inject(ModalService);
  private transloco = inject(TranslocoService);

  choices = input.required<IngredientChoice[] | undefined>();
  sizePrices = input.required<SizePrice[] | undefined>();
  orderedItem = input.required<OrderedItem>();
  orderableItem = input.required<OrderableItem>();  

  isSpecial = input.required<boolean>();
  firstInvalidChoice = input('');
  toggleWarningsAfterSubmission = input(false);
  expanded = true;
  iconNameType = IconNameType;
  selectedSize?: SizePrice;
  filteredChoices: IngredientChoice[] | undefined = undefined;
  filteredChoiceGroups: FilteredChoiceGroup[] = [];
  dependableChoices: Map<string, IngredientChoice[]> = new Map<string, IngredientChoice[]>();

  readonly resetWarningsAfterSubmissionValue = output();

  readonly updatedSpecialItem = output<OrderedItem>();

  availableSizePrices = computed<SizePrice[]>(() => {
    return this.sizePrices()?.filter((sizePrice) => !sizePrice.isUnavailable) ?? [];
  });

  hasMultipleTotalSizes = computed(() => (this.sizePrices()?.length ?? 0) > 1);

  shouldShowSizeSelector = computed(() => 
    !this.sizePricesHasSize() && 
    (this.availableSizePrices().length > 1 || 
    (this.hasMultipleTotalSizes() && this.availableSizePrices().length === 1))
  );

  shouldShowUnavailableMessage = computed(() => {
    const hasOutOfStockSize = this.sizePrices()?.filter((sizePrice) => sizePrice.isUnavailable) ?? [];
    return hasOutOfStockSize.length > 0;
  });

  getOrderedChoicesForGroup(groupName: string): OrderedChoice[] {
    const choices = this.orderableItem().choiceGroups.find((group) => group.name === groupName)?.choices;
    return this.orderedItem().choices.filter((choice) => choices?.includes(choice.name));
  }

  ngOnInit(): void {
    const availableSizes = this.availableSizePrices();
    if (availableSizes.length) {
      const matchingSize = availableSizes.find((s)=>s.size === this.orderedItem().size);
      this.selectedSize = matchingSize ?? availableSizes[0];
      if(this.selectedSize.isWebDefault){
        this.expanded = false;
      }
      if (!matchingSize && !this.isSpecial()) {
        this.changeSize(this.selectedSize);
        return;
      }
    }

    this.updateChoices(this.selectedSize?.size ?? '');
  }

  handleExpansion(value: boolean) {
    this.auditService.createAudit(() => `${value ? 'Expanded' : 'Collapsed'} Sizes on ${this.orderableItem()?.label} item`);
    this.expanded = value;
  }

  calMessage (size: SizePrice) {
    return Utils.getCalorieDisplay(this.orderableItem(), size); 
  }

  changeSize(size: SizePrice){
    const previousSize = this.selectedSize;
    this.selectedSize = size;

    const orderableItem = this.isSpecial() ? this.orderableItem() : this.itemEditorService.editingItem()?.orderableItem?.copy();
    const tempItem = this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem?.copy();
    const previousQuantity = tempItem.quantity;
    tempItem.size = this.selectedSize.size;
    if (size.remainingQuantity != null && tempItem.quantity > size.remainingQuantity) {
      tempItem.quantity = size.remainingQuantity;
    }

    this.auditService.createAudit(() => `Size changed to ${tempItem.size} for item ${orderableItem?.label}`);

    this.updateChoices(tempItem.size);
    this.removeUnavailableIngredientsFor(orderableItem, tempItem);
    this.handleDefaultsOnSizeChange(orderableItem, tempItem);

    if(this.isSpecial()){
      this.updatedSpecialItem.emit(tempItem);
    } else {
      this.itemEditorService.updateOrderedItem(tempItem)?.subscribe({
        error: (error) => {
          const errors = this.errorsService.parseErrorResponse(error).allErrors;
          const errorMessage = errors.length > 0 ? errors[0] : undefined;
          if (errorMessage && this.errorsService.isStockLimitError(errorMessage)) {
            const maxCountdown = this.errorsService.getAvailableQuantityFromError(errorMessage);
            if (maxCountdown !== undefined) {
              if (previousSize) {
                this.selectedSize = previousSize;
                tempItem.size = previousSize.size ?? '';
                tempItem.quantity = previousQuantity;
                this.updateChoices(previousSize.size ?? '');
                this.itemEditorService.updateOrderedItem(tempItem, false)?.subscribe();
              }
              const itemLabel = orderableItem?.label ?? orderableItem?.name;
              this.modalService.openGenericModal({
                modalTitle: maxCountdown === 0
                  ? this.transloco.translate('shared_components.out_of_stock.title')
                  : this.transloco.translate('shared_components.max_quantity.title'),
                modalMessage: maxCountdown === 0
                  ? this.transloco.translate('shared_components.out_of_stock.message', {item: itemLabel})
                  : this.transloco.translate('shared_components.max_quantity.message', {quantity: maxCountdown, item: itemLabel}),
                isAlert: true,
              });
            }
          }
        }
      });
    }
  }

  private removeUnavailableIngredientsFor(orderableItem: OrderableItem, tempItem: OrderedItem) {
    const orderedChoices: OrderedChoice[] = tempItem.choices;

    orderedChoices.forEach((orderedChoice: OrderedChoice) => {
      const orderableChoice = orderableItem.getChoice(orderedChoice.name);
      if (orderableChoice && !!orderedChoice.ingredients) {
        for (let i = orderedChoice.ingredients.length - 1; i >= 0; i--) {
          const orderableIngredient = orderableChoice.ingredients.find(ing => ing.name === orderedChoice.ingredients[i].ingredient);
          
          if (orderableIngredient && !orderableIngredient.getSizePrice(tempItem.size)) {
            this.removeIngredient(orderableIngredient, orderableChoice, tempItem);
          }
        }
      }
    });
  }

  getSizeAriaLabel(size: SizePrice): string {
    const parts: string[] = [size.label ?? ''];
    
    if (size?.servings) { parts.push(this.orderableItemService.getServings(size) ?? '');}
    const priceDiff = this.calculatePrice(size);
    if (!this.isSpecial() && priceDiff) { parts.push(priceDiff);}
    if (this.hasNoticePeriod(size)) { parts.push(this.calculatePrepTime(size.noticePeriod!));}
    if (size.caloriesFrom) { parts.push(this.calMessage(size)); }
    
    return parts.join(', ');
  }

  private handleDefaultsOnSizeChange(orderableItem: OrderableItem, tempItem: OrderedItem) {
    tempItem.choices.forEach(choice => {
      const orderableChoice = orderableItem.getChoice(choice.name);
      if (orderableChoice) {
        const tempChoiceIngredients = choice.ingredients ? choice.ingredients : [];
        orderableChoice.ingredients
          .filter(
            orderableIngredient =>
              orderableIngredient.isDefault &&
              orderableIngredient.getSizePrice(tempItem.size) &&
              this.itemEditorService.userChoices().find(uc => uc.name === choice.name)?.getIngredient(orderableIngredient.name) &&
              !tempChoiceIngredients.find(ing => ing.ingredient === orderableIngredient.name)
          )
          .map(orderableIngredient => OrderedIngredient.createFromOrderable(orderableIngredient))
          .forEach(orderedIngredient => {
            if (!choice.ingredients) {
              choice.ingredients = [];
            }
            choice.ingredients.push(orderedIngredient);
          });
      }
    })


  }

  private removeIngredient(orderableIngredient: OrderableIngredient, choice: any, tempItem: any) {
    const orderedChoice = tempItem.choices.find((c: any) => c.name === choice.name);

    const ingredient = orderedChoice.ingredients.find((ingredient: OrderedIngredient) => ingredient.ingredient === orderableIngredient.name);
    const ingredientIndex = orderedChoice.ingredients.indexOf(ingredient);
    
    orderedChoice.ingredients.splice(ingredientIndex, 1);
  }

  calculatePrice(size: SizePrice){
    let price;
    if(this.selectedSize){
      price = (size.price - this.selectedSize.price).toFixed(2);
    }
    return price && +price > 0 ? `+$${price}` : undefined;
  }

  calculatePrepTime(minutes: number){
    return `Prep Time: ${Utils.formatMinutesToHoursAndMinutes(minutes)}`;
  }

  hasNoticePeriod(size: SizePrice): boolean {
    return !!(size.noticePeriod && size.noticePeriod > 0) && !Utils.allNoticePeriodsSame(this.sizePrices() ?? []);
  }

  sizePricesHasSize(): boolean {
    const sizePrices = this.sizePrices();
    return sizePrices ? sizePrices.some(sizePrice => sizePrice.size === 'Size') : false;
  }

  updateChoices(size: string) {
    const allChoices = this.choices() ?? [];

    this.filteredChoices = this.choices()?.map((choice: IngredientChoice) => {
      const ingredients = choice.ingredients.filter((i) => i.getSizePrice(size));
      const temp = choice.copy();
      temp.ingredients = ingredients;
      return temp; 
    }).filter((choice: IngredientChoice) => !choice.dependsOn && choice.ingredients.length > 0);

    this.filteredChoiceGroups = this.orderableItem().choiceGroups.map((group) => {
      const groupChoices = this.filteredChoices?.filter((choice: IngredientChoice) => group.choices.includes(choice.name));
      return {
        name: group.name,
        choices: groupChoices ?? [],
      };
    }).filter((group) => group.choices.length > 0);
    this.filteredChoiceGroups.sort((a, b) => a.choices[0].ordinal - b.choices[0].ordinal);

    this.dependableChoices = this.dependableIngredientMappingService.buildAcyclicDependableChoicesMap(allChoices);
  }
}
